import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';

import { useSelector } from 'react-redux';
import CryptoJS from "crypto-js";
import { useNavigate } from 'react-router-dom';

/** Parse RECENT_SEARCHES row → query + provider */
const parseRecentSearchRow = (row) => {
  let searchData = row.search_data ?? row.SEARCH_DATA ?? {};

  if (typeof searchData === "string") {
    try {
      searchData = JSON.parse(searchData);
    } catch {
      searchData = { search_criteria: searchData };
    }
  }

  const query =
    searchData.search_criteria ||
    searchData.query ||
    (typeof searchData === "string" ? searchData : "") ||
    "";

  const provider = (
    row.provider ||
    row.PROVIDER ||
    "apollo"
  )
    .toString()
    .toLowerCase();

  return { query, provider, searchData };
}; 

const PROVIDER_LABELS = new Set([
  "apollo",
  "salestarget",
  "sales target",
  "sales_target",
]);

/** Build chips for UI from search_data / prompt text */
const getDisplayValues = (searchData, query) => {
  const displayValues = [];

  if (query && typeof query === "string") {
    // Prompt style: "Titles: HR Manager\nLocation: india\n..."
    query.split("\n").forEach((line) => {
      const t = line.trim();
      if (!t || /^get contacts for below criteria$/i.test(t)) return;
      // Prefer value after "Label: "
      const m = t.match(/^[^:]+:\s*(.+)$/);
      if (m?.[1]) {
        m[1].split(",").forEach((p) => {
          const v = p.trim();
          if (v) displayValues.push(v);
        });
      } else if (!PROVIDER_LABELS.has(t.toLowerCase())){
        displayValues.push(t);
      }
    });
  }

  if (displayValues.length === 0 && searchData && typeof searchData === "object") {
    Object.entries(searchData).forEach(([key, value]) => {
      if (
        key === "provider" ||
        key === "PROVIDER" ||
        key === "country_states" ||
        key === "search_criteria" ||
        key === "query"
      ) {
        return;
      }
      // if (key === "country_states" || key === "provider") return;
      if (Array.isArray(value) && value.length > 0) {
        displayValues.push(...value.map(String));
      } else if (typeof value === "string" && value.trim() !== "") {
        if (key === "search_criteria" || key === "query") return; // already handled
        displayValues.push(value.trim());
      }
    });
  }

  return displayValues.filter(
    (v) => !PROVIDER_LABELS.has(String(v).toLowerCase()));
};


const RecentSearchTable = () => {

  const { recentSearch } = useSelector(state => state.data);
  const navigate = useNavigate();

  const signInDetails =
    JSON.parse(sessionStorage.getItem("SignInDetails"));

  const role = signInDetails[0]?.role;

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateString;
    }
  };

  // const handleViewSearch = (item) => {
  //     let query = { query: item }
  //     const secret = "lm_data_param"; // Should be stored securely
  //     const ciphertext = CryptoJS.AES.encrypt(JSON.stringify(query), secret).toString();
  //     navigate(`/contact-filter?data=${encodeURIComponent(ciphertext)}`);
  // } 
  const handleViewSearch = (row) => {
    let { query, provider, searchData } = parseRecentSearchRow(row);
    if (!query) {
      console.warn("Recent search has no query", row);
      return;
    } 
    
    // Used by ContactFilterCategories / apiCallFunction
    sessionStorage.setItem("last_provider_used", provider);
    const decrypted = {
      query,
      provider, // ★ critical for search payload
      search_data: {
        ...searchData,
        provider,
      },
      session_id: null,
    };


    const secret = "lm_data_param"; // Should be stored securely
    const ciphertext = CryptoJS.AES.encrypt(JSON.stringify(decrypted), secret).toString();
    navigate(`/contact-filter?data=${encodeURIComponent(ciphertext)}`);
  }

  return (
    <div className='recent-search-table'>

      <div className='recent-search-table-card-header'>
        <div>
          <h5>Recent Searches</h5>
          <small>
            Last {recentSearch.length} search activities
          </small>
        </div>

        <div className='search-count-badge'>
          {recentSearch.length}
        </div>
      </div>

      <Paper className='recent-search-paper'>

        <TableContainer className='recent-section'>

          <Table stickyHeader size="small">

            <TableHead>
              <TableRow>

                {signInDetails[0].role === "super_admin" && (
                  <TableCell>User</TableCell>
                )}

                <TableCell>
                  Search Criteria
                </TableCell>

                <TableCell width="180">
                  Date
                </TableCell>

              </TableRow>
            </TableHead>

            <TableBody>

              {recentSearch.length > 0 ? (

                recentSearch.map((row, idx) => {

                  const { query, provider, searchData } =
                    parseRecentSearchRow(row);
                  const displayValues = getDisplayValues(query);


                  Object.entries(searchData).forEach(
                    ([key, value]) => {

                      if (
                        key === "country_states"
                      ) return;

                      if (
                        Array.isArray(value) &&
                        value.length > 0
                      ) {
                        displayValues.push(
                          ...value
                        );
                      }
                      else if (
                        typeof value === "string" &&
                        value.trim() !== ""
                      ) {
                        displayValues.push(
                          value.trim()
                        );
                      }
                    }
                  );

                  return (
                    <TableRow
                      key={row.id || idx}
                      className="recent-search-row"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleViewSearch(row)}
                    >


                      {signInDetails[0].role ===
                        "super_admin" && (
                          <TableCell>
                            <span className="user-badge">
                              {row.user_name}
                            </span>
                          </TableCell>
                        )}

                      <TableCell>

                        <div className='criteria-wrapper'>

                          <div className='criteria-icon'>
                            <i className='bx bx-search-alt'></i>
                          </div>

                          <div className='criteria-container'>

                            {displayValues
                              .slice(0, 5)
                              .map(
                                (
                                  item,
                                  index
                                ) => (
                                  <span
                                    key={index}
                                    className='criteria-chip'
                                    onClick={() => handleViewSearch(item)}
                                  >
                                    {item}
                                  </span>
                                )
                              )}

                            {displayValues.length > 5 && (
                              <span className='criteria-more'>
                                +
                                {displayValues.length - 5}
                              </span>
                            )}

                          </div>

                        </div>

                      </TableCell>

                      <TableCell>
                        <div className='date-cell'>
                          {formatDate(
                            row.date_time
                          )}
                        </div>
                      </TableCell>

                    </TableRow>
                  );
                })

              ) : (

                <TableRow className="empty-state-row">

                  <TableCell
                    colSpan={
                      signInDetails[0].role ===
                        "super_admin"
                        ? 3
                        : 2
                    }
                  >

                    <div className='empty-state'>

                      <i className='bx bx-search-alt'></i>

                      <h6>
                        No Recent Searches
                      </h6>

                      <p>
                        Your recent search
                        activity will appear here.
                      </p>

                    </div>

                  </TableCell>

                </TableRow>

              )}

            </TableBody>

          </Table>

        </TableContainer>

      </Paper>

    </div>
  );
};

export default RecentSearchTable;

// import {
//     Table, TableBody, TableCell, TableContainer,
//     TableHead, TableRow, Paper
// } from '@mui/material';
// import { useSelector } from 'react-redux';

// const RecentSearchTable = () => {
//     const { recentSearch } = useSelector(state => state.data);
//     const signInDetails = JSON.parse(sessionStorage.getItem("SignInDetails"));

//     return (
//         <div className='recent-search-table'>
//             <div className='recent-search-table-card-header'>
//                 <h5>Recent Search </h5>
//             </div>
//             <Paper style={{ height: "89%" }}>
//                 <TableContainer className='recent-section'>
//                     <Table size="small" aria-label="recent search table">
//                         <TableHead>
//                             <TableRow>
//                                 {signInDetails[0].role === 'super_admin' && <TableCell>User</TableCell>}
//                                 <TableCell>Search Criteria</TableCell>
//                                 <TableCell>Date</TableCell>
//                             </TableRow>
//                         </TableHead>
//                         <TableBody>
//                             {recentSearch.length !== 0 ? (
//                                 recentSearch.map((row, idx) => {
//                                     const searchData = row.search_data || {};
//                                     const displayValues = [];

//                                     Object.entries(searchData).forEach(([key, value]) => {
//                                         if (key === "country_states") return;

//                                         if (Array.isArray(value) && value.length > 0) {
//                                             displayValues.push(...value);
//                                         } else if (typeof value === "string" && value.trim() !== "") {
//                                             displayValues.push(value.trim());
//                                         }
//                                     });

//                                     return (
//                                         <TableRow key={idx}>
//                                             {signInDetails[0].role === 'super_admin' && <TableCell>{row.user_name}</TableCell>}
//                                             <TableCell>{displayValues.join(", ")}</TableCell>
//                                             <TableCell>{row.date_time}</TableCell>
//                                         </TableRow>
//                                     );
//                                 })
//                             ) : null}
//                         </TableBody>
//                     </Table>
//                 </TableContainer>
//             </Paper>
//         </div>
//     );
// };

// export default RecentSearchTable;
